/**
 * Front-end mirror of src/services/billing/donationRules.js
 * (docs/Decisions.md D44): the per-plan payment option set and the donation
 * fee breakdown, so the settings page and the donate page preview exactly
 * what the API will charge, to the cent, before anyone pays.
 *
 * The API is the authority: it re-validates every settings change and
 * checkout and recomputes the breakdown. This copy must stay identical in
 * behaviour; frontend/src/lib/payments.test.js imports both files and fails
 * if they disagree on any case in its table. Change both in the same commit.
 */

export const PREMIUM_PAYMENTS_FEATURE = 'payments-advanced';

/** Connect Express accounts are US accounts for now (D44); one currency. */
export const CURRENCY = 'usd';

/** Card includes the wallets Stripe enables for card (Apple Pay, Google Pay). */
export const PAYMENT_METHODS = ['card', 'us_bank_account', 'link'];
export const FREQUENCIES = ['one_time', 'month', 'year'];
export const RECURRING_INTERVALS = ['month', 'year'];

export const LIMITS = Object.freeze({
  /**
   * Hard floor and ceiling for any single donation: 1.00 to 100,000.00. The
   * floor keeps every fee below the gift itself (at 0.50 the card fee alone
   * nearly consumes it), and the ceiling keeps the total, with every fee at
   * its bound and covered by the donor, under Stripe's single-charge limit.
   * The limits a donor actually meets are the platform policy's
   * (DEFAULT_DONATION_LIMITS below, D53), which may move only inside these.
   */
  minDonationCents: 100,
  maxDonationCents: 10_000_000,
  maxPresetAmounts: 6,
  /**
   * The organization fee: a percentage of the gift, 0 to 10, that the org
   * keeps toward its own platform and processing costs (D60). The platform
   * policy's combined cap may allow less (orgFeePercentCap).
   */
  orgFeePercentMax: 10,
  orgFeeLabelMaxLength: 40,
  /** Sanity bound on a tier's transactionFeeRate when applied to a donation. */
  platformFeePercentMax: 50,
});

/**
 * The owner's per-gift limits (D50 item 5: 1.00 to 50,000.00), the built-in
 * default of the platform policy (src/shared/security/policy-settings.js
 * `donations.minAmountCents` / `maxAmountCents`, D53). A platform admin can
 * change them for every org or for one org; the policy arrives here through
 * getAvailablePaymentOptions(features, limits).
 */
export const DEFAULT_DONATION_LIMITS = Object.freeze({
  minAmountCents: 100,
  maxAmountCents: 5_000_000,
});

/**
 * Stripe's standard US pricing, used to ESTIMATE the processing fee the
 * platform pays on a destination charge and recovers through the application
 * fee (D44). Owner-confirmable: an account on custom pricing changes these.
 */
export const PROCESSING_FEES = Object.freeze({
  card: Object.freeze({ percent: 2.9, fixedCents: 30, capCents: null }),
  link: Object.freeze({ percent: 2.9, fixedCents: 30, capCents: null }),
  us_bank_account: Object.freeze({ percent: 0.8, fixedCents: 0, capCents: 500 }),
});

/** Stripe Billing's fee on recurring volume, charged to the platform. */
export const RECURRING_BILLING_PERCENT = 0.7;

export const DEFAULT_ORG_FEE_LABEL = 'Organization fee for platform and processing costs';
export const PLATFORM_FEE_LABEL = 'Platform fee';
export const PROCESSING_FEE_LABEL = 'Payment processing, estimated';
export const ORG_WORK_LABEL = "To the organization's work";
export const COVERED_FEES_LABEL = 'Platform and payment processing fees (covered by you)';

export const UPGRADE_MESSAGE =
  'Recurring donations, bank (ACH) payments, Link, custom amounts and donor-covered fees ' +
  'are part of the premium payments plan. Upgrade the organization plan to use them.';

export const DEFAULT_PAYMENT_SETTINGS = Object.freeze({
  enabled: false,
  currency: CURRENCY,
  presetAmountsCents: [1000, 2500, 5000, 10000],
  minimumAmountCents: 500,
  maximumAmountCents: 100000,
  allowCustomAmount: false,
  frequencies: ['one_time'],
  paymentMethods: ['card'],
  donorCoversFeesOption: false,
  orgFee: Object.freeze({ percent: 0, label: '' }),
});

const SETTINGS_KEYS = Object.keys(DEFAULT_PAYMENT_SETTINGS);

// ---------------------------------------------------------------------------
// Plan option set
// ---------------------------------------------------------------------------

function featureList(orgFeatures) {
  if (orgFeatures instanceof Set) return [...orgFeatures];
  return Array.isArray(orgFeatures) ? orgFeatures : [];
}

/** A policy's limits, kept inside the hard bounds and in order. */
export function normalizeDonationLimits(limits) {
  const clamp = (v, fallback) =>
    Number.isInteger(v)
      ? Math.min(Math.max(v, LIMITS.minDonationCents), LIMITS.maxDonationCents)
      : fallback;
  const max = clamp(limits?.maxAmountCents, DEFAULT_DONATION_LIMITS.maxAmountCents);
  const min = Math.min(clamp(limits?.minAmountCents, DEFAULT_DONATION_LIMITS.minAmountCents), max);
  return { minAmountCents: min, maxAmountCents: max };
}

/**
 * The platform policy's fee caps (D54 item 1, D60), the built-in defaults of
 * `donations.maxPlatformFeePercent` and `donations.maxCombinedFeePercent`.
 */
export const DEFAULT_FEE_CAPS = Object.freeze({
  maxPlatformFeePercent: 10,
  maxCombinedFeePercent: 15,
});

/** The platform fee actually charged: the tier or override rate, at most the cap. */
export function cappedPlatformFeePercent(rate, caps = DEFAULT_FEE_CAPS) {
  const cap = Number.isFinite(caps?.maxPlatformFeePercent)
    ? caps.maxPlatformFeePercent
    : DEFAULT_FEE_CAPS.maxPlatformFeePercent;
  return Math.min(rate, cap);
}

/**
 * The most an org may set as its own fee: the hard bound, and what the
 * combined cap leaves after the platform fee, in whole hundredths.
 */
export function orgFeePercentCap(platformFeePercent = 0, caps = DEFAULT_FEE_CAPS) {
  const combined = Number.isFinite(caps?.maxCombinedFeePercent)
    ? caps.maxCombinedFeePercent
    : DEFAULT_FEE_CAPS.maxCombinedFeePercent;
  const leftBps = Math.round(combined * 100) - Math.round(platformFeePercent * 100);
  return Math.max(0, Math.min(LIMITS.orgFeePercentMax * 100, leftBps)) / 100;
}

/**
 * The payment options an organization's plan allows, within the platform
 * policy's per-gift limits and fee caps.
 *
 * @param {string[]|Set<string>} orgFeatures - getOrgFeatures(org) on the API,
 *   the org's tier features on the front end
 * @param {{minAmountCents:number, maxAmountCents:number}} [limits] - the org's
 *   resolved policy limits (D53); the owner's defaults when omitted
 * @param {{platformFeePercent?: number, caps?: object}} [fees] - the org's
 *   platform fee and the policy's caps, which set `orgFeePercentMax`
 */
export function getAvailablePaymentOptions(
  orgFeatures,
  limits = DEFAULT_DONATION_LIMITS,
  { platformFeePercent = 0, caps = DEFAULT_FEE_CAPS } = {}
) {
  const premium = featureList(orgFeatures).includes(PREMIUM_PAYMENTS_FEATURE);
  return {
    limits: normalizeDonationLimits(limits),
    orgFeePercentMax: orgFeePercentCap(platformFeePercent, caps),
    plan: premium ? 'premium' : 'basic',
    paymentMethods: premium ? [...PAYMENT_METHODS] : ['card'],
    frequencies: premium ? [...FREQUENCIES] : ['one_time'],
    customAmount: premium,
    donorCoversFees: premium,
    orgFee: true,
    presetAmounts: true,
    maxPresetAmounts: LIMITS.maxPresetAmounts,
  };
}

// ---------------------------------------------------------------------------
// Money primitives
// ---------------------------------------------------------------------------

/**
 * A percentage (at most two decimal places) as integer basis points.
 * 2.9 -> 290, 10 -> 1000. Throws RangeError on anything else.
 */
export function percentToBps(percent, { max = 100 } = {}) {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    throw new RangeError('percent must be a finite number');
  }
  const bps = Math.round(percent * 100);
  if (Math.abs(percent * 100 - bps) > 1e-6) {
    throw new RangeError('percent may have at most two decimal places');
  }
  if (bps < 0 || bps > max * 100) {
    throw new RangeError(`percent must be between 0 and ${max}`);
  }
  return bps;
}

/** `bps` basis points of `cents`, rounded half-up to the cent (integers only). */
export function percentOfCents(cents, bps) {
  return Math.floor((cents * bps + 5000) / 10000);
}

/** Smallest integer q with q * d >= n, for non-negative integers. */
function ceilDiv(n, d) {
  let q = Math.floor(n / d);
  while (q * d < n) q += 1;
  while (q > 0 && (q - 1) * d >= n) q -= 1;
  return q;
}

/**
 * Stripe's processing fee on a charge of `totalCents` (estimate, D44).
 * Card and Link: 2.9 percent + 0.30. ACH: 0.8 percent capped at 5.00.
 * Recurring adds Stripe Billing's 0.7 percent.
 */
export function estimateProcessingFeeCents(
  totalCents,
  { paymentMethod = 'card', recurring = false } = {}
) {
  const schedule = PROCESSING_FEES[paymentMethod];
  if (!schedule) throw new RangeError(`Unknown payment method: ${paymentMethod}`);
  let fee = percentOfCents(totalCents, percentToBps(schedule.percent)) + schedule.fixedCents;
  if (schedule.capCents !== null && fee > schedule.capCents) fee = schedule.capCents;
  if (recurring) fee += percentOfCents(totalCents, percentToBps(RECURRING_BILLING_PERCENT));
  return fee;
}

/** "$12.34 USD": money always carries its currency (design rules). */
export function formatMoney(cents, currency = CURRENCY) {
  const value = (Number(cents) || 0) / 100;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(value);
  return `${formatted} ${currency.toUpperCase()}`;
}

// ---------------------------------------------------------------------------
// The breakdown
// ---------------------------------------------------------------------------

function assertCents(name, value, min, max) {
  if (!Number.isInteger(value)) throw new RangeError(`${name} must be an integer number of cents`);
  if (value < min || value > max) {
    throw new RangeError(`${name} must be between ${min} and ${max} cents`);
  }
}

/**
 * What a donation costs the donor and who receives what, in integer cents.
 * Every fee comes out of the gift (D50 item 3); nothing is added on top
 * except the fees a donor chooses to cover.
 *
 * - donationCents: the gift the donor chose.
 * - platformFeeCents: the platform fee rate (percent) of the gift.
 * - processingFeeCents: Stripe's fee on the total charged (estimate).
 * - orgFeeCents: the organization fee (D60), a percentage of the gift the
 *   org keeps toward its own platform and processing costs. It is part of
 *   what the org receives, not an extra charge, and is disclosed separately
 *   so the donor sees which fees are the platform's and which are the
 *   organization's. Never more than the org receives.
 * - coveredFeesCents: with donorCoversFees, the smallest extra amount for
 *   which the org still nets the whole donation after the platform and
 *   processing fees; 0 otherwise.
 * - totalCents: what the donor is charged.
 * - applicationFeeCents: platform fee + processing fee, collected by the
 *   platform as `application_fee_amount` on the destination charge.
 * - applicationFeePercent: the same as a percent of the total (rounded UP to
 *   two places) for subscriptions, which take `application_fee_percent`.
 * - netToOrgCents: total - application fee (the org fee included).
 * - toOrgWorkCents: netToOrgCents - orgFeeCents.
 * - disclosure: every part of the total with its recipient, in order.
 *
 * Throws RangeError on out-of-bounds input; callers validate first.
 */
export function computeDonationBreakdown({
  amountCents,
  orgFee = {},
  platformFeeRate = 0,
  donorCoversFees = false,
  paymentMethod = 'card',
  recurring = false,
} = {}) {
  assertCents('amountCents', amountCents, LIMITS.minDonationCents, LIMITS.maxDonationCents);
  const orgBps = percentToBps(orgFee.percent ?? 0, { max: LIMITS.orgFeePercentMax });
  const platformBps = percentToBps(platformFeeRate, { max: LIMITS.platformFeePercentMax });
  if (!PROCESSING_FEES[paymentMethod])
    throw new RangeError(`Unknown payment method: ${paymentMethod}`);

  const processing = total => estimateProcessingFeeCents(total, { paymentMethod, recurring });

  const donationCents = amountCents;
  const platformFeeCents = percentOfCents(donationCents, platformBps);

  let totalCents = donationCents;
  if (donorCoversFees) {
    // No total below start + processing(start) can net the gift (processing
    // only grows with the total), so scan upward from there to the first that does.
    const start = donationCents + platformFeeCents;
    totalCents = start + processing(start);
    let guard = 0;
    while (totalCents - platformFeeCents - processing(totalCents) < donationCents) {
      totalCents += 1;
      guard += 1;
      if (guard > 100000) throw new Error('Fee coverage did not converge');
    }
  }

  const processingFeeCents = processing(totalCents);
  const applicationFeeCents = platformFeeCents + processingFeeCents;
  const netToOrgCents = totalCents - applicationFeeCents;
  if (netToOrgCents < 0) throw new RangeError('Fees exceed the amount charged');
  const orgFeeCents = Math.min(percentOfCents(donationCents, orgBps), netToOrgCents);
  const toOrgWorkCents = netToOrgCents - orgFeeCents;
  const coveredFeesCents = totalCents - donationCents;
  const applicationFeePercent = ceilDiv(applicationFeeCents * 10000, totalCents) / 100;

  const lineItems = [{ key: 'donation', label: 'Donation', amountCents: donationCents }];
  if (coveredFeesCents > 0) {
    lineItems.push({
      key: 'covered_fees',
      label: COVERED_FEES_LABEL,
      amountCents: coveredFeesCents,
    });
  }

  const disclosure = [
    {
      key: 'platform_fee',
      label: PLATFORM_FEE_LABEL,
      recipient: 'platform',
      percent: platformBps / 100,
      amountCents: platformFeeCents,
    },
    {
      key: 'processing_fee',
      label: PROCESSING_FEE_LABEL,
      recipient: 'stripe',
      percent: null,
      amountCents: processingFeeCents,
    },
  ];
  if (orgFeeCents > 0) {
    disclosure.push({
      key: 'org_fee',
      label: (orgFee.label || '').trim() || DEFAULT_ORG_FEE_LABEL,
      recipient: 'organization',
      percent: orgBps / 100,
      amountCents: orgFeeCents,
    });
  }
  disclosure.push({
    key: 'org_work',
    label: ORG_WORK_LABEL,
    recipient: 'organization',
    percent: null,
    amountCents: toOrgWorkCents,
  });

  return {
    currency: CURRENCY,
    donationCents,
    orgFeeCents,
    coveredFeesCents,
    totalCents,
    platformFeeCents,
    processingFeeCents,
    applicationFeeCents,
    applicationFeePercent,
    netToOrgCents,
    toOrgWorkCents,
    donorCoversFees: Boolean(donorCoversFees),
    paymentMethod,
    recurring: Boolean(recurring),
    lineItems,
    disclosure,
    rates: {
      orgFeePercent: orgBps / 100,
      platformFeePercent: platformBps / 100,
    },
  };
}

// ---------------------------------------------------------------------------
// Settings validation
// ---------------------------------------------------------------------------

function isBool(v) {
  return typeof v === 'boolean';
}

/**
 * Validate a whole payment-settings object against the plan's options.
 *
 * @returns {{ value: object, errors: string[], premiumViolations: string[] }}
 *   `value` is the normalized settings (presets sorted, de-duplicated).
 */
export function validatePaymentSettings(input, options) {
  const errors = [];
  const premiumViolations = [];
  const s = input && typeof input === 'object' ? input : {};

  const unknown = Object.keys(s).filter(k => !SETTINGS_KEYS.includes(k));
  if (unknown.length) errors.push(`Unknown settings: ${unknown.join(', ')}`);

  if (!isBool(s.enabled)) errors.push('enabled must be true or false');
  if (s.currency !== CURRENCY) errors.push(`currency must be ${CURRENCY}`);
  const limits = options.limits || normalizeDonationLimits();
  const min = s.minimumAmountCents;
  const minOk =
    Number.isInteger(min) && min >= limits.minAmountCents && min <= limits.maxAmountCents;
  if (!minOk) {
    errors.push(
      `minimumAmountCents must be a whole number of cents from ${limits.minAmountCents} to ${limits.maxAmountCents}`
    );
  }

  if (!isBool(s.allowCustomAmount)) {
    errors.push('allowCustomAmount must be true or false');
  } else if (s.allowCustomAmount && !options.customAmount) {
    premiumViolations.push('customAmount');
  }

  const max = s.maximumAmountCents;
  const maxOk = Number.isInteger(max) && max <= limits.maxAmountCents && (!minOk || max >= min);
  if (!maxOk) {
    errors.push(
      `maximumAmountCents must be a whole number of cents, at least the minimum and at most ${limits.maxAmountCents}`
    );
  }

  let presets = [];
  if (!Array.isArray(s.presetAmountsCents)) {
    errors.push('presetAmountsCents must be a list of amounts in cents');
  } else {
    presets = [...new Set(s.presetAmountsCents)].sort((a, b) => a - b);
    if (presets.length < 1 || presets.length > LIMITS.maxPresetAmounts) {
      errors.push(`Offer between 1 and ${LIMITS.maxPresetAmounts} preset amounts`);
    }
    for (const p of presets) {
      if (!Number.isInteger(p) || p < limits.minAmountCents || p > limits.maxAmountCents) {
        errors.push(`Preset amount ${p} is not a valid amount in cents`);
      } else if (minOk && p < min) {
        errors.push('Every preset amount must be at least the minimum amount');
      } else if (s.allowCustomAmount === true && maxOk && p > max) {
        errors.push('Every preset amount must be at most the maximum amount');
      }
    }
  }

  if (!Array.isArray(s.frequencies) || s.frequencies.length === 0) {
    errors.push('frequencies must list at least one_time');
  } else {
    for (const f of s.frequencies) {
      if (!FREQUENCIES.includes(f)) errors.push(`Unknown frequency: ${f}`);
      else if (!options.frequencies.includes(f) && !premiumViolations.includes('recurring')) {
        premiumViolations.push('recurring');
      }
    }
    if (!s.frequencies.includes('one_time')) errors.push('frequencies must include one_time');
  }

  if (!Array.isArray(s.paymentMethods) || s.paymentMethods.length === 0) {
    errors.push('paymentMethods must list at least card');
  } else {
    for (const m of s.paymentMethods) {
      if (!PAYMENT_METHODS.includes(m)) errors.push(`Unknown payment method: ${m}`);
      else if (!options.paymentMethods.includes(m)) premiumViolations.push(m);
    }
    if (!s.paymentMethods.includes('card')) errors.push('paymentMethods must include card');
  }

  if (!isBool(s.donorCoversFeesOption)) {
    errors.push('donorCoversFeesOption must be true or false');
  } else if (s.donorCoversFeesOption && !options.donorCoversFees) {
    premiumViolations.push('donorCoversFees');
  }

  const fee = s.orgFee;
  const orgFeeMax = Number.isFinite(options.orgFeePercentMax)
    ? options.orgFeePercentMax
    : LIMITS.orgFeePercentMax;
  if (!fee || typeof fee !== 'object') {
    errors.push('orgFee must be an object with percent and label');
  } else {
    const extra = Object.keys(fee).filter(k => !['percent', 'label', 'fixedCents'].includes(k));
    if (extra.length) errors.push(`Unknown orgFee field(s): ${extra.join(', ')}`);
    // A fixed amount was the retired add-on fee (D54 item 1); 0 is accepted
    // so settings saved before D60 can be saved again.
    if (fee.fixedCents !== undefined && fee.fixedCents !== 0) {
      errors.push(
        'The organization fee is a percentage of the gift; a fixed amount is not offered'
      );
    }
    let bps = null;
    try {
      bps = percentToBps(fee.percent, { max: LIMITS.orgFeePercentMax });
    } catch {
      errors.push(`orgFee.percent must be from 0 to ${orgFeeMax} with at most two decimal places`);
    }
    if (bps !== null && bps > Math.round(orgFeeMax * 100)) {
      errors.push(
        `orgFee.percent can be at most ${orgFeeMax}: the platform and organization fees together are capped`
      );
    }
    if (typeof fee.label !== 'string' || fee.label.length > LIMITS.orgFeeLabelMaxLength) {
      errors.push(`orgFee.label must be text of at most ${LIMITS.orgFeeLabelMaxLength} characters`);
    }
  }

  const value = {
    ...s,
    presetAmountsCents: presets,
    frequencies: Array.isArray(s.frequencies)
      ? FREQUENCIES.filter(f => s.frequencies.includes(f))
      : s.frequencies,
    paymentMethods: Array.isArray(s.paymentMethods)
      ? PAYMENT_METHODS.filter(m => s.paymentMethods.includes(m))
      : s.paymentMethods,
    orgFee:
      fee && typeof fee === 'object'
        ? {
            percent: fee.percent,
            label: typeof fee.label === 'string' ? fee.label.trim() : fee.label,
          }
        : fee,
  };

  return { value, errors, premiumViolations };
}

/**
 * Settings as a donor may use them: premium choices an org saved while on a
 * premium plan are dropped once the plan no longer allows them.
 */
export function effectivePaymentSettings(settings, options) {
  const s = { ...DEFAULT_PAYMENT_SETTINGS, ...(settings || {}) };
  const limits = options.limits || normalizeDonationLimits();
  const frequencies = s.frequencies.filter(f => options.frequencies.includes(f));
  const paymentMethods = s.paymentMethods.filter(m => options.paymentMethods.includes(m));
  // The org's own range, kept inside the platform policy's (which a platform
  // admin may have tightened since the org saved its settings).
  const minimumAmountCents = Math.min(
    Math.max(s.minimumAmountCents, limits.minAmountCents),
    limits.maxAmountCents
  );
  const maximumAmountCents = Math.max(
    Math.min(s.maximumAmountCents, limits.maxAmountCents),
    minimumAmountCents
  );
  const presets = s.presetAmountsCents.filter(
    p => p >= minimumAmountCents && p <= limits.maxAmountCents
  );
  return {
    ...s,
    minimumAmountCents,
    maximumAmountCents,
    presetAmountsCents: presets.length ? presets : [minimumAmountCents],
    frequencies: frequencies.length ? frequencies : ['one_time'],
    paymentMethods: paymentMethods.length ? paymentMethods : ['card'],
    allowCustomAmount: Boolean(s.allowCustomAmount && options.customAmount),
    donorCoversFeesOption: Boolean(s.donorCoversFeesOption && options.donorCoversFees),
    // Kept under the combined cap, which a platform admin may have lowered
    // since the org saved its fee (D60).
    orgFee: {
      percent: Math.min(
        Number.isFinite(s.orgFee?.percent) ? s.orgFee.percent : 0,
        Number.isFinite(options.orgFeePercentMax)
          ? options.orgFeePercentMax
          : LIMITS.orgFeePercentMax
      ),
      label: typeof s.orgFee?.label === 'string' ? s.orgFee.label : '',
    },
  };
}

/**
 * Validate one quote or checkout request against the org's saved settings
 * and its plan.
 *
 * @returns {{ errors: string[], premiumViolations: string[] }}
 */
export function validateDonationRequest(request, settings, options) {
  const errors = [];
  const premiumViolations = [];
  const {
    amountCents,
    frequency = 'one_time',
    paymentMethod = 'card',
    donorCoversFees = false,
  } = request || {};
  const s = { ...DEFAULT_PAYMENT_SETTINGS, ...(settings || {}) };
  const limits = options.limits || normalizeDonationLimits();
  const minimum = Math.max(s.minimumAmountCents, limits.minAmountCents);

  if (!FREQUENCIES.includes(frequency)) {
    errors.push(`frequency must be one of: ${FREQUENCIES.join(', ')}`);
  } else if (!options.frequencies.includes(frequency)) {
    premiumViolations.push('recurring');
  } else if (!s.frequencies.includes(frequency)) {
    errors.push('This organization does not offer that donation frequency');
  }

  if (!PAYMENT_METHODS.includes(paymentMethod)) {
    errors.push(`paymentMethod must be one of: ${PAYMENT_METHODS.join(', ')}`);
  } else if (!options.paymentMethods.includes(paymentMethod)) {
    premiumViolations.push(paymentMethod);
  } else if (!s.paymentMethods.includes(paymentMethod)) {
    errors.push('This organization does not accept that payment method');
  }

  if (typeof donorCoversFees !== 'boolean') {
    errors.push('donorCoversFees must be true or false');
  } else if (donorCoversFees) {
    if (!options.donorCoversFees) premiumViolations.push('donorCoversFees');
    else if (!s.donorCoversFeesOption)
      errors.push('This organization does not offer covering fees');
  }

  if (!Number.isInteger(amountCents)) {
    errors.push('amountCents must be a whole number of cents');
  } else if (amountCents < minimum) {
    errors.push(`The minimum donation is ${formatMoney(minimum)}`);
  } else if (amountCents > limits.maxAmountCents) {
    errors.push(`The maximum donation is ${formatMoney(limits.maxAmountCents)}`);
  } else if (!s.presetAmountsCents.includes(amountCents)) {
    if (!options.customAmount) {
      premiumViolations.push('customAmount');
    } else if (!s.allowCustomAmount) {
      errors.push('Choose one of the preset amounts');
    } else if (amountCents > Math.min(s.maximumAmountCents, limits.maxAmountCents)) {
      errors.push(
        `The maximum donation is ${formatMoney(Math.min(s.maximumAmountCents, limits.maxAmountCents))}`
      );
    }
  }

  return { errors, premiumViolations };
}
